
import { EventEmitter, Inject, Injectable} from "@angular/core";

import { Client, EmploymentContract } from "@salaxy/core";

import { ClientNg } from "../api/ClientNg";
import { SessionService } from "../account/SessionService";
import { IRepository } from "./IRepository";

/**
 * Implements the salaxy repository pattern for Contracts stored for the current account.
 */
@Injectable()
export class ContractRepository implements IRepository<EmploymentContract> {

    /** List of items in the repository */
    public list: EmploymentContract[] = [];

    /** Currently selected item in the repository */
    public current: EmploymentContract = null;

    constructor(
        @Inject(ClientNg) private client: Client,
    ) { }

    /**
     * Assures the repository initialization:
     * Initialization is done after successful authentication.
     * Typically the call to fetch the repository list, but may fetch other stuff from the server.
     *
     * @param session - The session service that contains information about the authenticated user.
     */
    public init(session: SessionService) {
        if (session.companyAccount || (session.personAccount && session.personAccount.isEmployer)) {
            this.reloadList();
        }
    }

    /**
     * Reloads the list from the server - called e.g. after delete and add new
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public reloadList(callback?: () => any): void {
        this.client.contractsApi.getContracts().then((contracts) => {
            this.list = contracts;
            if (callback) {
                callback();
            }
        });
    }

    /**
     * Gets a new blank object with default values, suitable for UI binding.
     * This is a synchronous method - does not go to the server and it is not 100% reliable in that way.
     * However, it shoud provide a basic object, good enough for most views.
     */
    public getBlank(): EmploymentContract {
        // TODO: Go-through and make sensible defaults
        const blank = {
        };
        return blank;
    }

    /**
     * Sets the current repository item.
     * If not loaded, starts loading from the server to the current.
     * @param id - Identifier of the calculation
     */
    public setCurrentId(id: string) {
        for (const contract of this.list) {
            if (contract.id === id) {
                this.setCurrent(contract);
                return;
            }
        }
        this.setCurrent(null);
    }

    /**
     * Sets the current calculation
     * @param item - Item to set as current
     */
    public setCurrent(item: EmploymentContract) {
        this.current = item;
    }

    /**
     * Creates a new item and sets it as current for editing and saving.
     * The item is not yet sent to server and thus it is not in the list, nor does it have an identifier.
     */
    public newCurrent() {
        const isSelfWorker = false;
        this.client.contractsApi.createNewContract(isSelfWorker, (item) => {
            this.setCurrent(item);
        });
    }

    /**
     * Saves changes to the current item.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public saveCurrent(callback?: () => any): void {
        if (this.current) {
            this.save(this.current, callback);
        } else {
            // console.error("SaveCurrent called when current is not set.");
        }
    }

    /**
     * Adds or updates a given item.
     * Operation is determined based on id: null/''/'new' adds, other string values update.
     * @param item - Item that is updated.
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public save(item: EmploymentContract, callback?: () => any): void {
        this.client.contractsApi.saveContract(item)
        .then((savedObject) => {
            this.setCurrent(savedObject);
            this.reloadList(callback);
        });
    }

    /**
     * Deletes the given item from repository if possible
     * @param id - Identifier of the item to delete
     * @param callback - Optional callback that is called after save and susequent reloadList completes.
     */
    public delete(id: string, callback?: () => any) {
        this.client.contractsApi.deleteContract(id)
            .then(() => {
                this.reloadList(callback);
            });
    }

    /**
     * Gets the preview HTML for an employment contract.
     * @param id - Identifier of the contract to get preview for
     * @param callback - Callback that is called with the preview HTML after the preview has been fetched
     */
    public getPreview(id: string, callback: (previewHTML: any) => any) {
        // HACK: Add HTML sanitizer.
        // TODO: Add post method for non-saved contract (potentially anonymous).

        this.client.contractsApi.getPreviewById(id)
            .then((previewHTML) => {
                callback(previewHTML);
            });
    }

    /**
     * Gets an URL for the Employment contract preview.
     * May be used in linked previews (to new window) or iframes (you may need to sanitize the URL).
     * @param id IDentifier of the contract.
     */
    public getPreviewUrl(id: string) {
        return this.client.ajax.getServerAddress() + "/reporthtml/employmentcontract/" + id + "?access_token=" + this.client.ajax.getCurrentToken();
    }
}
